/**
 * Colonna destra del client email: dettaglio del messaggio selezionato con
 * toolbar azioni (rispondi/inoltra/letto/sposta/archivia/elimina) e
 * download allegati (lazy dal server). Il corpo HTML viene SEMPRE
 * sanificato con DOMPurify (anti-XSS obbligatorio, piano DEC-089).
 */

import React, { useState } from 'react';
import {
  Archive,
  ChevronDown,
  CornerUpLeft,
  Forward,
  FolderInput,
  Mail,
  MailOpen,
  Paperclip,
  ShieldAlert,
  Trash2,
} from 'lucide-react';
import DOMPurify from 'dompurify';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
} from '@floraltech/ui';
import { useAuth } from '@shell/AuthContext';
import {
  emailFetch,
  type EmailAllegato,
  type EmailCartella,
  type EmailEtichetta,
  type EmailMessaggioDetail,
} from '../lib/api';
import { EtichettaChip } from './EtichettaChip';
import { EtichettePicker } from './EtichettePicker';
import AllegatoAnteprimaDialog, { anteprimaSupportata } from './AllegatoAnteprimaDialog';
import SalvaDocumentaleDialog from './SalvaDocumentaleDialog';
import CaricaSistemaDialog from './CaricaSistemaDialog';

function formatDimensione(bytes: number | null): string {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function indirizzi(lista: { nome: string | null; email: string }[] | null): string {
  if (!lista || lista.length === 0) return '';
  return lista.map((d) => (d.nome ? `${d.nome} <${d.email}>` : d.email)).join(', ');
}

interface Props {
  messaggio: EmailMessaggioDetail | null;
  loading: boolean;
  cartelle: EmailCartella[];
  cartellaCorrenteId: string | null;
  onRispondi: (m: EmailMessaggioDetail) => void;
  onInoltra: (m: EmailMessaggioDetail) => void;
  onToggleLetto: (m: EmailMessaggioDetail) => void;
  onSposta: (m: EmailMessaggioDetail, cartellaId: string) => void;
  onArchivia: (m: EmailMessaggioDetail) => void;
  onElimina: (m: EmailMessaggioDetail) => void;
  /** Casella PEC senza email.pec_gestione: nasconde tutta la toolbar azioni. */
  soloLettura?: boolean;
  /** Etichette note (id -> dato), per risolvere gli id del messaggio e per il picker. */
  etichette?: Map<string, EmailEtichetta>;
  /** Rimuove un'etichetta dal messaggio corrente. Se assente (o soloLettura) niente X di rimozione. */
  onRimuoviEtichetta?: (etichettaId: string) => void;
  /** Applica un'etichetta al messaggio corrente. Se assente (o soloLettura) niente picker in toolbar. */
  onApplicaEtichetta?: (etichettaId: string) => void;
}

export function MessaggioViewer({
  messaggio,
  loading,
  cartelle,
  cartellaCorrenteId,
  onRispondi,
  onInoltra,
  onToggleLetto,
  onSposta,
  onArchivia,
  onElimina,
  soloLettura = false,
  etichette = new Map(),
  onRimuoviEtichetta,
  onApplicaEtichetta,
}: Props) {
  const { hasPermesso } = useAuth();
  const [downloadInCorso, setDownloadInCorso] = useState<string | null>(null);
  const [anteprima, setAnteprima] = useState<EmailAllegato | null>(null);
  const [azioneAllegato, setAzioneAllegato] = useState<
    { tipo: 'documentale' | 'sistema'; allegato: EmailAllegato } | null
  >(null);

  if (loading) {
    return <p className="p-6 text-sm text-muted-foreground">Caricamento...</p>;
  }
  if (!messaggio) {
    return (
      <p className="p-6 text-sm text-muted-foreground">
        Seleziona un messaggio per leggerlo.
      </p>
    );
  }

  const htmlSanificato = messaggio.corpo_html
    ? DOMPurify.sanitize(messaggio.corpo_html, {
        FORBID_TAGS: ['style', 'form', 'input', 'button'],
        FORBID_ATTR: ['onerror', 'onload', 'onclick'],
      })
    : null;

  const dataStr = messaggio.data_ricezione
    ? new Date(messaggio.data_ricezione).toLocaleString('it-IT')
    : '';

  const scaricaAllegato = async (allegatoId: string, filename: string | null) => {
    setDownloadInCorso(allegatoId);
    try {
      const res = await emailFetch(
        `/messaggi/${messaggio.id}/allegati/${allegatoId}/download`
      );
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename ?? 'allegato';
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloadInCorso(null);
    }
  };

  const destinazioni = cartelle.filter((c) => c.id !== cartellaCorrenteId);

  return (
    <article className="flex h-full flex-col overflow-y-auto p-4">
      {!soloLettura && (
        <div className="flex flex-wrap items-center gap-1 border-b pb-2">
          <Button variant="outline" size="sm" onClick={() => onRispondi(messaggio)}>
            <CornerUpLeft className="mr-1 h-4 w-4" />
            Rispondi
          </Button>
          <Button variant="outline" size="sm" onClick={() => onInoltra(messaggio)}>
            <Forward className="mr-1 h-4 w-4" />
            Inoltra
          </Button>
          <Tooltip label={messaggio.flag_letto ? 'Segna come da leggere' : 'Segna come letto'}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onToggleLetto(messaggio)}
              aria-label={messaggio.flag_letto ? 'Segna come da leggere' : 'Segna come letto'}
            >
              {messaggio.flag_letto ? (
                <Mail className="h-4 w-4" />
              ) : (
                <MailOpen className="h-4 w-4" />
              )}
            </Button>
          </Tooltip>
          <DropdownMenu>
            <Tooltip label="Sposta in cartella">
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" aria-label="Sposta in cartella">
                  <FolderInput className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
            </Tooltip>
            <DropdownMenuContent>
              {destinazioni.map((c) => (
                <DropdownMenuItem key={c.id} onClick={() => onSposta(messaggio, c.id)}>
                  {c.nome_display ?? c.nome_imap}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Tooltip label="Archivia">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onArchivia(messaggio)}
              aria-label="Archivia"
            >
              <Archive className="h-4 w-4" />
            </Button>
          </Tooltip>
          <Tooltip label="Elimina">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onElimina(messaggio)}
              aria-label="Elimina"
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </Tooltip>
          {onApplicaEtichetta && (
            <EtichettePicker
              etichette={[...etichette.values()].filter(
                (e) => e.attiva && !(messaggio.etichette ?? []).includes(e.id)
              )}
              tooltip="Applica etichetta"
              onSeleziona={(e) => onApplicaEtichetta(e.id)}
            />
          )}
        </div>
      )}

      <header className="border-b py-3">
        <h2 className="text-lg font-semibold">{messaggio.oggetto || '(senza oggetto)'}</h2>
        <div className="mt-1 space-y-0.5 text-sm text-muted-foreground">
          <p>
            <span className="font-medium text-foreground">
              {messaggio.da_nome || messaggio.da_email}
            </span>{' '}
            {messaggio.da_nome && messaggio.da_email && <>&lt;{messaggio.da_email}&gt;</>}
          </p>
          {messaggio.a_json && messaggio.a_json.length > 0 && (
            <p>A: {indirizzi(messaggio.a_json)}</p>
          )}
          {messaggio.cc_json && messaggio.cc_json.length > 0 && (
            <p>Cc: {indirizzi(messaggio.cc_json)}</p>
          )}
          <p>{dataStr}</p>
        </div>
        {(messaggio.etichette ?? []).length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {(messaggio.etichette ?? []).map((id) => {
              const e = etichette.get(id);
              if (!e) return null;
              return (
                <EtichettaChip
                  key={id}
                  etichetta={e}
                  onRimuovi={
                    !soloLettura && onRimuoviEtichetta ? () => onRimuoviEtichetta(id) : undefined
                  }
                />
              );
            })}
          </div>
        )}
        {messaggio.allegati.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {messaggio.allegati.map((a) => {
              const sospetto =
                a.phishing_verdict != null &&
                (a.phishing_verdict as { safe?: boolean }).safe === false;
              const clickPrincipale = () =>
                anteprimaSupportata(a.content_type)
                  ? setAnteprima(a)
                  : scaricaAllegato(a.id, a.filename);
              return (
                <div key={a.id} className="inline-flex items-center gap-0.5">
                  <Tooltip
                    label={sospetto ? 'Allegato segnalato come rischioso' : 'Apri allegato'}
                  >
                    <button
                      type="button"
                      onClick={clickPrincipale}
                      disabled={downloadInCorso === a.id}
                    >
                      <Badge variant={sospetto ? 'destructive' : 'outline'} className="gap-1">
                        {sospetto ? (
                          <ShieldAlert className="h-3 w-3" />
                        ) : (
                          <Paperclip className="h-3 w-3" />
                        )}
                        {downloadInCorso === a.id ? 'Scarico...' : (a.filename ?? 'allegato')}
                        {a.dimensione != null && (
                          <span className="text-muted-foreground">
                            ({formatDimensione(a.dimensione)})
                          </span>
                        )}
                      </Badge>
                    </button>
                  </Tooltip>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        aria-label="Azioni allegato"
                        className="rounded p-0.5 text-muted-foreground hover:bg-muted"
                      >
                        <ChevronDown className="h-3 w-3" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        disabled={!anteprimaSupportata(a.content_type)}
                        onClick={() => setAnteprima(a)}
                      >
                        Anteprima
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => scaricaAllegato(a.id, a.filename)}>
                        Scarica in locale
                      </DropdownMenuItem>
                      {hasPermesso('documentale.crea') && (
                        <DropdownMenuItem
                          onClick={() => setAzioneAllegato({ tipo: 'documentale', allegato: a })}
                        >
                          Salva nel Documentale
                        </DropdownMenuItem>
                      )}
                      {hasPermesso('ordini.crea') && (
                        <DropdownMenuItem
                          onClick={() => setAzioneAllegato({ tipo: 'sistema', allegato: a })}
                        >
                          Carica nel sistema
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              );
            })}
          </div>
        )}
      </header>
      <div className="flex-1 pt-4">
        {htmlSanificato ? (
          <div
            className="prose prose-sm max-w-none dark:prose-invert [&_a]:break-all"
            // Contenuto sanificato con DOMPurify qui sopra: mai HTML grezzo.
            dangerouslySetInnerHTML={{ __html: htmlSanificato }}
          />
        ) : (
          <pre className="whitespace-pre-wrap break-words font-sans text-sm">
            {messaggio.corpo_testo || '(messaggio vuoto)'}
          </pre>
        )}
      </div>
      <AllegatoAnteprimaDialog
        messaggioId={messaggio.id}
        allegato={anteprima}
        onClose={() => setAnteprima(null)}
      />
      <SalvaDocumentaleDialog
        messaggioId={messaggio.id}
        allegato={azioneAllegato?.tipo === 'documentale' ? azioneAllegato.allegato : null}
        onClose={() => setAzioneAllegato(null)}
      />
      <CaricaSistemaDialog
        messaggioId={messaggio.id}
        allegato={azioneAllegato?.tipo === 'sistema' ? azioneAllegato.allegato : null}
        onClose={() => setAzioneAllegato(null)}
      />
    </article>
  );
}
